import { useCallback, useEffect, useRef, useState } from 'react';
import { capturaApi, mensagemErroCaptura } from '@/features/captura/api/capturaApi';
import type {
  CamposCapturaDTO,
  FotoCapturaDTO,
  MensagemServidorCaptura,
  ModoCaptura,
  SessaoCapturaDTO,
} from '@/features/captura/tipos';

export type EstadoSessaoCaptura =
  | 'inativa'
  | 'conectando'
  | 'aguardando'
  | 'celular-conectado'
  /** O socket falhou várias vezes seguidas; o hook continua tentando. */
  | 'sem-conexao'
  | 'expirada'
  | 'erro';

export type AbrirSessaoCaptura = {
  modo: ModoCaptura;
  orcamentoId?: string | null;
  placa?: string | null;
  numero?: string | null;
};

export type OpcoesSessaoCaptura = {
  /** O celular enviou placa/marca/modelo/ano/motor/KM conferidos. */
  aoReceberCampos?: (campos: CamposCapturaDTO) => void;
  /** Uma foto nova subiu (do celular, ao vivo). */
  aoReceberFoto?: (foto: FotoCapturaDTO) => void;
  /** Uma foto foi apagada (no celular ou em outra tela). */
  aoRemoverFoto?: (id: string) => void;
};

export type SessaoCaptura = {
  sessao: SessaoCapturaDTO | null;
  estado: EstadoSessaoCaptura;
  /** Celulares com a página aberta agora. */
  celulares: number;
  /** Fotos recebidas por esta(s) sessão(ões) desde que o hook montou. */
  fotos: FotoCapturaDTO[];
  erro: string | null;
  /** Últimos campos enviados pelo celular (para o status do modal). */
  ultimosCampos: CamposCapturaDTO | null;
  /** Cria a sessão sob demanda — ou reaproveita a atual enquanto não expirar. */
  abrir: (parametros: AbrirSessaoCaptura) => Promise<SessaoCapturaDTO | null>;
  /** Avisa o celular que o formulário foi preenchido. */
  confirmarCamposAplicados: () => void;
  /** Encerra no servidor (o celular é avisado) e volta a `inativa`. */
  encerrar: () => Promise<void>;
  /** Gera um código novo com os mesmos parâmetros do último `abrir`. */
  recriar: () => Promise<SessaoCapturaDTO | null>;
  /** Todas as sessões criadas por este hook — para vincular as fotos ao salvar. */
  idsSessoes: () => string[];
};

const PING_MS = 20_000;
/** Falhas seguidas (≈ 7 s com o backoff) até trocar "Conectando…" pelo aviso. */
export const FALHAS_ATE_AVISO = 3;
const BACKOFF_INICIAL_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;
/** Só reaproveita a sessão com folga — senão o QR expira na mão do mecânico. */
const FOLGA_REUSO_MS = 5 * 60_000;
/** setTimeout estoura acima de ~24,8 dias; a sessão dura 12h, mas por garantia. */
const MAX_TIMEOUT_MS = 2_147_000_000;

const WS_CONECTANDO = 0;
const WS_ABERTO = 1;

/** Espera antes da tentativa `n` de reconectar: 1s, 2s, 4s… até 30s. */
export function atrasoReconexao(tentativa: number): number {
  return Math.min(BACKOFF_MAX_MS, BACKOFF_INICIAL_MS * 2 ** Math.max(0, tentativa));
}

/**
 * Página em https não abre `ws://` (o navegador recusa como conteúdo
 * misto): se o servidor devolver o esquema errado atrás de um proxy, sobe
 * para `wss://` em vez de girar para sempre.
 */
export function urlSocketSegura(url: string, protocoloDaPagina?: string): string {
  return protocoloDaPagina === 'https:' && /^ws:/i.test(url) ? `wss:${url.slice(3)}` : url;
}

function mesclarFoto(lista: FotoCapturaDTO[], foto: FotoCapturaDTO): FotoCapturaDTO[] {
  const idx = lista.findIndex((f) => f.id === foto.id);
  if (idx === -1) return [...lista, foto];
  const copia = lista.slice();
  copia[idx] = foto;
  return copia;
}

function podeReaproveitar(atual: SessaoCapturaDTO, p: AbrirSessaoCaptura): boolean {
  if ((atual.orcamentoId ?? null) !== (p.orcamentoId ?? null)) return false;
  const expira = Date.parse(atual.expiraEm);
  return Number.isFinite(expira) && expira - Date.now() > FOLGA_REUSO_MS;
}

export function useSessaoCaptura(opcoes: OpcoesSessaoCaptura = {}): SessaoCaptura {
  const [sessao, setSessao] = useState<SessaoCapturaDTO | null>(null);
  const [estado, setEstado] = useState<EstadoSessaoCaptura>('inativa');
  const [celulares, setCelulares] = useState(0);
  const [fotos, setFotos] = useState<FotoCapturaDTO[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [ultimosCampos, setUltimosCampos] = useState<CamposCapturaDTO | null>(null);

  const opcoesRef = useRef(opcoes);
  const sessaoRef = useRef<SessaoCapturaDTO | null>(null);
  const parametrosRef = useRef<AbrirSessaoCaptura | null>(null);
  const criandoRef = useRef<Promise<SessaoCapturaDTO | null> | null>(null);
  const idsRef = useRef<string[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const expiradaRef = useRef(false);
  const fotosRef = useRef<FotoCapturaDTO[]>([]);
  const montadoRef = useRef(true);

  // Callbacks sempre na versão mais recente, sem reabrir o socket a cada render.
  useEffect(() => {
    opcoesRef.current = opcoes;
  });

  useEffect(() => {
    fotosRef.current = fotos;
  }, [fotos]);

  useEffect(() => {
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
    };
  }, []);

  const criarNova = useCallback(async (p: AbrirSessaoCaptura) => {
    setEstado('conectando');
    setErro(null);
    try {
      const nova = await capturaApi.criarSessao(p);
      if (!montadoRef.current) {
        // A tela fechou enquanto o código era gerado: não deixa sessão órfã aberta.
        void capturaApi.encerrarSessao(nova.id).catch(() => undefined);
        return null;
      }
      idsRef.current = [...idsRef.current, nova.id];
      sessaoRef.current = nova;
      expiradaRef.current = false;
      setSessao(nova);
      setCelulares(0);
      setUltimosCampos(null);
      return nova;
    } catch (e) {
      if (montadoRef.current) {
        setEstado('erro');
        setErro(mensagemErroCaptura(e, 'Não foi possível gerar o código. Tente de novo.'));
      }
      return null;
    }
  }, []);

  const abrir = useCallback(
    async (p: AbrirSessaoCaptura) => {
      parametrosRef.current = p;
      const atual = sessaoRef.current;
      if (atual && !expiradaRef.current && podeReaproveitar(atual, p)) return atual;
      if (criandoRef.current) return criandoRef.current;
      const promessa = criarNova(p).finally(() => {
        criandoRef.current = null;
      });
      criandoRef.current = promessa;
      return promessa;
    },
    [criarNova],
  );

  const recriar = useCallback(async () => {
    const p = parametrosRef.current;
    if (!p) return null;
    const antiga = sessaoRef.current;
    sessaoRef.current = null;
    // Fecha o socket antigo antes de encerrar no servidor, para o aviso
    // `encerrada` da sessão velha não pintar a nova de "expirada".
    setSessao(null);
    if (antiga) void capturaApi.encerrarSessao(antiga.id).catch(() => undefined);
    expiradaRef.current = false;
    if (criandoRef.current) return criandoRef.current;
    const promessa = criarNova(p).finally(() => {
      criandoRef.current = null;
    });
    criandoRef.current = promessa;
    return promessa;
  }, [criarNova]);

  const encerrar = useCallback(async () => {
    const atual = sessaoRef.current;
    sessaoRef.current = null;
    expiradaRef.current = false;
    setSessao(null);
    setEstado('inativa');
    setCelulares(0);
    setFotos([]);
    setUltimosCampos(null);
    setErro(null);
    if (atual) {
      try {
        await capturaApi.encerrarSessao(atual.id);
      } catch {
        // Sem rede: a sessão expira sozinha no servidor em 12h.
      }
    }
  }, []);

  const confirmarCamposAplicados = useCallback(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WS_ABERTO) {
      try {
        ws.send(JSON.stringify({ tipo: 'campos-aplicados' }));
      } catch {
        // socket caindo — o celular só deixa de ver o "✓ aplicado"
      }
    }
  }, []);

  const idsSessoes = useCallback(() => [...idsRef.current], []);

  // ---- WebSocket da sessão atual ----
  useEffect(() => {
    if (!sessao) return;
    const sessaoAtual = sessao;
    let ativo = true;
    let encerrada = false;
    let tentativas = 0;
    let jaConectou = false;
    let socket: WebSocket | null = null;
    let timerReconexao: ReturnType<typeof setTimeout> | undefined;
    let timerPing: ReturnType<typeof setInterval> | undefined;
    const expiraMs = Date.parse(sessaoAtual.expiraEm);
    const expirou = () => Number.isFinite(expiraMs) && Date.now() >= expiraMs;
    expiradaRef.current = false;

    const marcarExpirada = () => {
      expiradaRef.current = true;
      setEstado('expirada');
      setCelulares(0);
    };

    const pararPing = () => {
      if (timerPing) clearInterval(timerPing);
      timerPing = undefined;
    };

    const fecharSocket = () => {
      const s = socket;
      socket = null;
      if (wsRef.current === s) wsRef.current = null;
      pararPing();
      if (s && (s.readyState === WS_CONECTANDO || s.readyState === WS_ABERTO)) {
        try {
          s.close(1000);
        } catch {
          // já estava fechando
        }
      }
    };

    /** Depois de uma queda, busca o que chegou enquanto o socket estava fora. */
    const recarregarFotos = () => {
      capturaApi
        .listarFotos({ sessaoId: sessaoAtual.id })
        .then((lista) => {
          if (!ativo || !Array.isArray(lista)) return;
          const conhecidas = new Set(fotosRef.current.map((f) => f.id));
          setFotos((atual) => [
            ...atual.filter((f) => f.sessaoId !== sessaoAtual.id),
            ...lista,
          ]);
          for (const f of lista) {
            if (!conhecidas.has(f.id)) opcoesRef.current.aoReceberFoto?.(f);
          }
        })
        .catch(() => undefined);
    };

    const tratar = (bruto: unknown) => {
      let msg: MensagemServidorCaptura;
      try {
        msg = JSON.parse(String(bruto)) as MensagemServidorCaptura;
      } catch {
        return;
      }
      if (!msg || typeof msg !== 'object') return;
      switch (msg.tipo) {
        case 'conectado':
          tentativas = 0;
          if (jaConectou) recarregarFotos();
          jaConectou = true;
          setEstado('aguardando');
          break;
        case 'presenca': {
          const n = Math.max(0, Number(msg.celulares) || 0);
          setCelulares(n);
          setEstado(n > 0 ? 'celular-conectado' : 'aguardando');
          break;
        }
        case 'campos': {
          const dados = msg.dados ?? {};
          setUltimosCampos(dados);
          opcoesRef.current.aoReceberCampos?.(dados);
          break;
        }
        case 'foto':
          if (msg.foto && msg.foto.id) {
            const foto = msg.foto;
            setFotos((lista) => mesclarFoto(lista, foto));
            opcoesRef.current.aoReceberFoto?.(foto);
          }
          break;
        case 'foto-removida': {
          const id = msg.id;
          setFotos((lista) => lista.filter((f) => f.id !== id));
          opcoesRef.current.aoRemoverFoto?.(id);
          break;
        }
        case 'encerrada':
          encerrada = true;
          marcarExpirada();
          fecharSocket();
          break;
        default:
          break;
      }
    };

    /** "Conectando…" nas primeiras tentativas; depois, o aviso de sem conexão. */
    const mostrarTentativa = () => {
      setEstado(tentativas >= FALHAS_ATE_AVISO ? 'sem-conexao' : 'conectando');
    };

    const agendarReconexao = () => {
      if (!ativo || encerrada) return;
      mostrarTentativa();
      setCelulares(0);
      const atraso = atrasoReconexao(tentativas);
      tentativas += 1;
      if (timerReconexao) clearTimeout(timerReconexao);
      timerReconexao = setTimeout(conectar, atraso);
    };

    function conectar() {
      if (!ativo || encerrada) return;
      if (expirou()) {
        marcarExpirada();
        return;
      }
      mostrarTentativa();
      let s: WebSocket;
      try {
        s = new WebSocket(
          urlSocketSegura(
            sessaoAtual.wsUrl,
            typeof location === 'undefined' ? undefined : location.protocol,
          ),
        );
      } catch {
        agendarReconexao();
        return;
      }
      socket = s;
      wsRef.current = s;
      s.onopen = () => {
        if (socket !== s) return;
        pararPing();
        timerPing = setInterval(() => {
          if (s.readyState !== WS_ABERTO) return;
          try {
            s.send(JSON.stringify({ tipo: 'ping' }));
          } catch {
            // o close cuida
          }
        }, PING_MS);
      };
      s.onmessage = (ev: MessageEvent) => {
        if (socket === s) tratar(ev.data);
      };
      s.onerror = () => {
        // O `close` vem logo depois e decide se reconecta.
      };
      s.onclose = (ev: CloseEvent) => {
        if (socket !== s) return; // fechado de propósito (troca de sessão/unmount)
        socket = null;
        if (wsRef.current === s) wsRef.current = null;
        pararPing();
        if (!ativo || encerrada) return;
        if (ev.code === 4401 || expirou()) {
          // Token recusado: sessão expirada/encerrada no servidor. Reconectar não adianta.
          encerrada = true;
          marcarExpirada();
          return;
        }
        agendarReconexao();
      };
    }

    conectar();

    const restante = Number.isFinite(expiraMs) ? Math.max(0, expiraMs - Date.now()) : null;
    const timerExpiracao =
      restante !== null && restante < MAX_TIMEOUT_MS
        ? setTimeout(() => {
            if (!ativo) return;
            encerrada = true;
            marcarExpirada();
            fecharSocket();
          }, restante)
        : undefined;

    return () => {
      ativo = false;
      if (timerReconexao) clearTimeout(timerReconexao);
      if (timerExpiracao) clearTimeout(timerExpiracao);
      fecharSocket();
    };
  }, [sessao]);

  return {
    sessao,
    estado,
    celulares,
    fotos,
    erro,
    ultimosCampos,
    abrir,
    confirmarCamposAplicados,
    encerrar,
    recriar,
    idsSessoes,
  };
}
